import { ReactNode, FC, useState, useRef, Children, createRef, useCallback } from 'react'

import { GlowHeading, Grid, Link, Button, Placeholder } from '~ui'
import { useDidUpdate } from '~hooks'
import SliderItem from './Item'

import styles from './Slider.module.scss'

const SliderPlaceholder = ({ rows, cardHeight, cardWidth }) => (
  <div className={styles.wrapper}>
    <div
      style={{
        gridTemplateRows: `repeat(${rows}, ${cardHeight}px)`,
      }}
      className={styles.slider}
    >
      {Array(10 * rows)
        .fill(null)
        .map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Placeholder key={i} className={styles.placeholderItem} style={{ width: cardWidth }} />
        ))}
    </div>
  </div>
)

const CustomButtonGroupAsArrows = ({ title, seeAllLink, seeAllCount, next, previous, loading }) => (
  <Grid
    className={styles['slider-container-controls']}
    gridAutoFlow="column"
    justifyContent="space-between"
    alignItems="center"
  >
    {title &&
      (seeAllLink ? (
        <span className={styles['slider-container-controls-title']}>
          <Link href={seeAllLink} color="g-white">
            {title}
          </Link>
        </span>
      ) : (
        <span className={styles['slider-container-controls-title']}>
          <GlowHeading glow={false} Tag="h2" fill>
            {title}
          </GlowHeading>
        </span>
      ))}
    <Grid gridAutoFlow="column" alignItems="center" gap={34}>
      {seeAllLink && (
        <Link href={seeAllLink} bold>
          {`See all${seeAllCount ? ` (${seeAllCount})` : ''}`}
        </Link>
      )}
      <Grid
        gridAutoFlow="column"
        alignItems="center"
        gap={12}
        className={styles['slider-container-controls-buttons']}
      >
        <Button onClick={previous} icon="arrow" loading={loading} disabled={loading} />
        <Button onClick={next} icon="arrow" loading={loading} disabled={loading} />
      </Grid>
    </Grid>
  </Grid>
)

type SliderProps = {
  items: Array<ReactNode>
  title: string
  seeAllLink?: string
  seeAllCount?: string | number
  cardWidth?: number
  cardHeight?: number
  loading?: boolean
  rows?: number
}

const Slider: FC<SliderProps> = ({
  title,
  seeAllCount,
  seeAllLink,
  items,
  loading,
  cardWidth = 270,
  cardHeight = 348,
  rows = 1,
}) => {
  const [selected, setSelected] = useState(0)
  const total = Children.count(items)

  const rootRef = useRef<HTMLDivElement>(null)

  const onNext = () => {
    setSelected((prev) => {
      if (total > selected + rows) {
        return prev + rows
      }

      return 0
    })
  }

  const onBack = () =>
    setSelected((prev) => {
      if (selected > 0) {
        return prev - rows
      }

      return total - rows
    })

  const refs = Children.toArray(items).reduce((acc, value, index) => {
    acc[index] = createRef<HTMLDivElement>()
    return acc
  }, {})

  const handleScroll = useCallback(
    (value: number) => {
      if (!refs[value]?.current) return

      refs[value].current?.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'nearest',
      })
    },
    [refs]
  )

  useDidUpdate(() => {
    handleScroll(selected)
  }, [selected])

  return (
    <Grid className={styles['slider-container']}>
      <CustomButtonGroupAsArrows
        title={title}
        seeAllCount={seeAllCount}
        seeAllLink={seeAllLink}
        next={onNext}
        previous={onBack}
        loading={loading}
      />
      {loading ? (
        <SliderPlaceholder rows={rows} cardHeight={cardHeight} cardWidth={cardWidth} />
      ) : (
        <div className={styles.wrapper}>
          <div
            style={{
              gridTemplateRows: `repeat(${rows}, ${cardHeight}px)`,
            }}
            className={styles.slider}
            ref={rootRef}
          >
            {Children.map(items, (child, index) => (
              <div
                className={[styles.item, index === selected && styles.active].join(' ')}
                style={{
                  width: cardWidth,
                }}
                onClick={() => handleScroll(index)}
                // eslint-disable-next-line react/no-array-index-key
                key={`${styles.item}-${index}`}
                data-index={index}
                ref={refs[index]}
                aria-hidden
              >
                {child}
              </div>
            ))}
          </div>
        </div>
      )}
    </Grid>
  )
}

const SliderComponent = Slider as typeof Slider & {
  Item: typeof SliderItem
}

SliderComponent.Item = SliderItem

export default SliderComponent
